import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { gql, useQuery } from '@apollo/client';
import { useState } from 'react';
import ApplicationAnalytics from './components/ApplicationAnalytics';
import FinancialAnalytics from './components/FinancialAnalytics';
import UserAnalytics from './components/UserAnalytics';

const GET_APPLICATION_ANALYTICS = gql`
   query GetApplicationAnalytics($timeFilter: String!) {
      getApplicationAnalytics(timeFilter: $timeFilter) {
         applicationTypes {
            id
            label
            value
         }
         statusData {
            id
            label
            value
         }
         weeklyVolume {
            day
            count
         }
      }
   }
`;

const GET_FINANCIAL_ANALYTICS = gql`
   query GetFinancialAnalytics($timeFilter: String!) {
      getFinancialAnalytics(timeFilter: $timeFilter) {
         revenueStats {
            total
            paid
            pending
            overdue
         }
      }
   }
`;

const GET_USER_ANALYTICS = gql`
   query GetUserAnalytics($timeFilter: String!) {
      getUserAnalytics(timeFilter: $timeFilter) {
         totalUsers
         activeUsers
         newUsers {
            day
            count
         }
         usersByStatus {
            id
            label
            value
         }
      }
   }
`;

export default function PersonnelReportsPage() {
   const [timeFilter, setTimeFilter] = useState('week');

   const { loading: appLoading, error: appError, data: appData } = useQuery(GET_APPLICATION_ANALYTICS, {
      variables: { timeFilter }
   });

   const { loading: financialLoading, error: financialError, data: financialData } = useQuery(GET_FINANCIAL_ANALYTICS, {
      variables: { timeFilter }
   });

   const { loading: userLoading, error: userError, data: userData } = useQuery(GET_USER_ANALYTICS, {
      variables: { timeFilter }
   });

   if (appLoading || financialLoading || userLoading)
      return (
         <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 pt-20 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto mt-8">
               <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-10">
                  <Skeleton className="h-10 w-48" />
                  <Skeleton className="h-10 w-[180px]" />
               </div>

               <div className="space-y-4">
                  <div className="grid w-full grid-cols-3 gap-2 p-1 bg-muted rounded-lg">
                     {[1, 2, 3].map((tab) => (
                        <Skeleton key={tab} className="h-10" />
                     ))}
                  </div>

                  <div className="grid gap-4">
                     <Skeleton className="h-[300px] w-full" />

                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map((stat) => (
                           <Skeleton key={stat} className="h-32" />
                        ))}
                     </div>
                  </div>
               </div>
            </div>
         </div>
      );

   if (appError || financialError || userError)
      return (
         <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 pt-20 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto mt-8">
               <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-red-700 dark:text-red-300">Error loading analytics data. Please try again later.</p>
               </div>
            </div>
         </div>
      );

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 pt-20 pb-8 px-4 sm:px-6 lg:px-8">
         <div className="max-w-7xl mx-auto mt-8">
            <div className="flex flex-col mb sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-10">
               <h1 className="text-2xl sm:text-4xl font-bold text-black dark:text-white">Analytics Dashboard</h1>
               <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="w-[180px]">
                     <SelectValue placeholder="Select time period" />
                  </SelectTrigger>
                  <SelectContent>
                     <SelectItem value="week">This Week</SelectItem>
                     <SelectItem value="month">This Month</SelectItem>
                     <SelectItem value="year">This Year</SelectItem>
                     <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
               </Select>
            </div>

            <Tabs defaultValue="applications" className="space-y-4">
               <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="applications">Application Analytics</TabsTrigger>
                  <TabsTrigger value="financial">Financial Analytics</TabsTrigger>
                  <TabsTrigger value="users">User Analytics</TabsTrigger>
               </TabsList>

               <TabsContent value="applications">
                  <ApplicationAnalytics data={appData.getApplicationAnalytics} />
               </TabsContent>

               <TabsContent value="financial">
                  <FinancialAnalytics data={financialData.getFinancialAnalytics} />
               </TabsContent>

               <TabsContent value="users">
                  <UserAnalytics data={userData.getUserAnalytics} />
               </TabsContent>
            </Tabs>
         </div>
      </div>
   );
}
